/* eslint-disable no-console */
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

import { isClientError, isNotFoundError } from "@/api/errors";

import { AppModeSchema, getEnvironmentVariable } from "./env";

class Logger {
  constructor(private appMode: AppModeSchema) {
    this.setUser = this.setUser.bind(this);
    this.log = this.log.bind(this);
    this.error = this.error.bind(this);
    this.isRemoteLoggingEnabled = this.isRemoteLoggingEnabled.bind(this);
    this.isTestRun = this.isTestRun.bind(this);

    if (this.isRemoteLoggingEnabled()) {
      Sentry.init({
        beforeSend(e, hint) {
          if (
            hint.originalException &&
            isNotFoundError(hint.originalException)
          ) {
            return null;
          }

          if (
            hint.originalException &&
            isClientError(hint.originalException) &&
            hint.originalException.status === 409 &&
            (hint.originalException.message as string).match(
              /token for user already exists/i,
            )
          ) {
            return null;
          }

          return e;
        },
        dsn: "https://9ca524635148db6f53ce6c67ff523e31@o82643.ingest.us.sentry.io/4507186468290560",
        environment: this.appMode,
        ignoreErrors: [/Failed to fetch/i],
        integrations: [
          Sentry.reactRouterV6BrowserTracingIntegration({
            createRoutesFromChildren,
            matchRoutes,
            useEffect,
            useLocation,
            useNavigationType,
          }),
          Sentry.replayIntegration({
            blockAllMedia: false,
            // keep credential / PII inputs masked even with global unmask (GDPR)
            mask: ['input[type="password"]', 'input[type="email"]'],
            maskAllInputs: false,
            maskAllText: false,
          }),
          Sentry.httpClientIntegration(),
        ],
        normalizeDepth: 10,
        release: getEnvironmentVariable("RELEASE_ID"),
        replaysOnErrorSampleRate: 1.0,
        replaysSessionSampleRate: 0.01,
        sendDefaultPii: false,
        tracePropagationTargets: [
          "localhost",
          /^https:\/\/[^/]*\.cleverfarm\.cz/, // nightly/staging API only
          /^https:\/\/[^/]*\.cleveranalytics\.ag/, // production API only
        ],
        tracesSampleRate: 0.25,
      });
    }
  }

  public setContext(context: Parameters<typeof Sentry.setContext>[1]) {
    if (!this.isRemoteLoggingEnabled()) {
      return;
    }

    Sentry.setContext("details", context);
  }

  public setUser(user: Sentry.User | null) {
    if (!this.isRemoteLoggingEnabled()) {
      return;
    }
    Sentry.setUser(user);
  }

  public log(...args: unknown[]) {
    if (this.isTestRun()) {
      return;
    }
    console.log(...args);
  }

  public error(e: Error | unknown | undefined, ...args: unknown[]) {
    if (this.isTestRun()) {
      return;
    }
    console.error(e, ...args);

    if (this.isRemoteLoggingEnabled()) {
      return Sentry.captureException(e);
    }
  }

  private isRemoteLoggingEnabled() {
    return this.appMode !== "local";
  }

  private isTestRun() {
    return import.meta.env.MODE === "test";
  }
}

const logger = new Logger(getEnvironmentVariable("APP_MODE"));

export { logger };
